/**
 * The block builder's Blocks panel: the prebuilt block catalog (blocks/catalog.ts), searchable and
 * grouped by category. Every item drags into the editor (PALETTE_BLOCK_MIME, handled by
 * blocks/paletteInsert.ts's PaletteDrop) or inserts after the cursor's block on click/Enter.
 * The Team blocks tab is the team library (TeamBlocksTab.tsx).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { Editor } from '@tiptap/core'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { BLOCK_CATALOG, BLOCK_CATEGORIES, searchCatalog, type CatalogBlock } from '../../blocks/catalog'
import { insertCatalogBlock, setPaletteUploadHandler, writePaletteBlock } from '../../blocks/paletteInsert'
import { uploadAndInsertImage } from './imageUpload'
import TeamBlocksTab from './TeamBlocksTab'
import type { NoteSink } from '../../blocks/teamBlocks'

interface Props {
  editor: Editor | null
  templateId: string
  versionId?: string | null
  onClose?: () => void
  /** Viewers browse team blocks but cannot insert them. */
  canEdit?: boolean
  /** Bumped after a block is saved, so the Team blocks tab refetches. */
  teamRefreshKey?: number
  /** Where an inserted team block's footnotes/endnotes/comments go. */
  noteSink?: NoteSink
  initialTab?: Tab
}

export type Tab = 'catalog' | 'team'

export default function BlocksPanel({ editor, templateId, versionId, onClose, canEdit = true, teamRefreshKey, noteSink, initialTab = 'catalog' }: Props) {
  const [tab, setTab] = useState<Tab>(initialTab)
  const [query, setQuery] = useState('')
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const uploadPosRef = useRef<{ pos: number; replaceEmpty: boolean } | null>(null)

  useEffect(() => {
    if (!editor) return
    setPaletteUploadHandler(editor, (pos, replaceEmpty) => {
      uploadPosRef.current = { pos, replaceEmpty }
      fileRef.current?.click()
    })
    return () => setPaletteUploadHandler(editor, null)
  }, [editor])

  function onFile(file: File) {
    const at = uploadPosRef.current
    if (!editor || at === null) return
    setUploading(true)
    uploadAndInsertImage(editor, templateId, file, at.pos, '', at.replaceEmpty)
      .catch(() => {})
      .finally(() => { setUploading(false); uploadPosRef.current = null })
  }

  const insert = (b: CatalogBlock) => { if (editor) insertCatalogBlock(editor, b) }
  const results = searchCatalog(query, BLOCK_CATALOG)

  return (
    <aside aria-label="Blocks" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: colors.surface, fontSize: fontSize.sm }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: `${spacing.sm} ${spacing[3]}`, borderBottom: `1px solid ${colors.border}` }}>
        <span style={{ fontWeight: 600, color: colors.text, flex: 1 }}>Blocks</span>
        {onClose && (
          <button type="button" aria-label="Close blocks panel" onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: fontSize.base }}>×</button>
        )}
      </div>
      <div role="tablist" style={{ display: 'flex', gap: 4, padding: `${spacing.sm} ${spacing[3]} 0` }}>
        <TabBtn active={tab === 'catalog'} onClick={() => setTab('catalog')}>Library</TabBtn>
        <TabBtn active={tab === 'team'} onClick={() => setTab('team')}>Team blocks</TabBtn>
      </div>
      <input ref={fileRef} type="file" accept="image/*" data-testid="palette-image-input" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = '' }} />

      {tab === 'catalog' ? (
        <div style={{ flex: 1, overflow: 'auto', padding: `${spacing.sm} ${spacing[3]}` }}>
          <input
            type="search"
            aria-label="Search blocks"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search blocks"
            style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none' }}
          />
          <div style={{ color: colors.textMuted, fontSize: fontSize.xs, margin: `${spacing.sm} 0` }}>
            {uploading ? 'Uploading image…' : 'Drag into the document, or click to insert after the cursor.'}
          </div>
          {results.length === 0 && <div style={{ color: colors.textMuted, padding: '3px 0' }}>No blocks match “{query}”.</div>}
          {BLOCK_CATEGORIES.map(cat => {
            const items = results.filter(b => b.category === cat)
            if (!items.length) return null
            return (
              <section key={cat} aria-label={cat}>
                <SectionTitle>{cat}</SectionTitle>
                {items.map(b => <BlockItem key={b.id} block={b} onInsert={insert} />)}
              </section>
            )
          })}
        </div>
      ) : (
        <TeamBlocksTab editor={editor} templateId={templateId} versionId={versionId} canEdit={canEdit} refreshKey={teamRefreshKey} noteSink={noteSink} />
      )}
    </aside>
  )
}

function BlockItem({ block, onInsert }: { block: CatalogBlock; onInsert: (b: CatalogBlock) => void }) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      data-block-id={block.id}
      title={block.upload ? `${block.name} — choose a file` : `Insert ${block.name}`}
      onDragStart={e => writePaletteBlock(e.dataTransfer, block)}
      onClick={() => onInsert(block)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInsert(block) } }}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: radius.sm, cursor: 'grab', userSelect: 'none' }}
      onMouseEnter={e => { e.currentTarget.style.background = colors.surfaceMist }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      <span aria-hidden style={{ flex: '0 0 26px', height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.background, color: colors.textSecondary, fontSize: fontSize.xs, overflow: 'hidden' }}>{block.icon}</span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ color: colors.text, fontWeight: 600 }}>{block.name}</span>
        <span style={{ color: colors.textMuted, fontSize: fontSize.xs, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.description}</span>
      </span>
    </div>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick}
      style={{ padding: '4px 10px', border: `1px solid ${active ? colors.primary : colors.border}`, borderRadius: radius.sm, background: active ? colors.primaryLight : 'none', color: active ? colors.primary : colors.textSecondary, fontSize: fontSize.xs, fontWeight: 600, cursor: 'pointer' }}>
      {children}
    </button>
  )
}

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <div style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', margin: `${spacing[3]} 0 ${spacing.xs}` }}>{children}</div>
)
